import { createTheme } from '@mui/material/styles';
import {
  parchmentStack,
  elevatedPaperSurface,
  sidebarStack,
  VINTAGE_SIDEBAR,
} from './vintageSurfaces';

/** Event detail info card typography (see EventDetailPageSections). */
export const PRIMARY_DETAIL_TEXT = '#3d2f26';
export const DETAIL_MUTED = '#5a5045';

const lora = '"Lora", "Georgia", "Times New Roman", serif';
const sans = ['Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(',');

const cardShadow =
  '0 4px 24px rgba(42, 35, 25, 0.1), 0 2px 6px rgba(42, 35, 25, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.45)';

// Create a Material UI theme — vintage / parchment admin shell
const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2f5d45',
      light: '#4a7c62',
      dark: '#1e3f2e',
      contrastText: '#faf8f3',
    },
    secondary: {
      main: '#9a7238',
      light: '#b8894a',
      dark: '#6e5228',
      contrastText: '#1a1510',
    },
    error: {
      main: '#a53a32',
    },
    info: {
      main: '#2a6b6b',
    },
    success: {
      main: '#3d6b4a',
    },
    warning: {
      main: '#9a7a24',
    },
    background: {
      default: '#e4dcc8',
      paper: '#f2ebe0',
      darkBg: '#121212',
    },
    text: {
      primary: '#2a2319',
      secondary: 'rgba(42, 35, 25, 0.68)',
    },
    divider: 'rgba(90, 80, 69, 0.22)',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: sans,
    fontWeightMedium: 500,
    h1: {
      fontFamily: lora,
      fontWeight: 600,
    },
    h2: {
      fontFamily: lora,
      fontWeight: 600,
    },
    h3: {
      fontFamily: lora,
      fontWeight: 600,
    },
    h4: {
      fontFamily: lora,
      fontWeight: 600,
    },
    h5: {
      fontFamily: lora,
      fontWeight: 600,
    },
    h6: {
      fontFamily: lora,
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: lora,
    },
    subtitle2: {
      fontFamily: lora,
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          minHeight: '100vh',
        },
        body: ({ theme }) => ({
          minHeight: '100vh',
          ...parchmentStack(theme.palette.background.default),
          margin: 0,
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'default',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ...elevatedPaperSurface(theme.palette.background.paper),
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...sidebarStack(VINTAGE_SIDEBAR.main),
          color: VINTAGE_SIDEBAR.contrastText,
          borderRight: '1px solid rgba(0, 0, 0, 0.28)',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ...elevatedPaperSurface(theme.palette.background.paper),
          color: theme.palette.text.primary,
        }),
        elevation1: {
          boxShadow:
            '0 2px 12px rgba(42, 35, 25, 0.09), 0 1px 3px rgba(42, 35, 25, 0.06)',
        },
        elevation2: {
          boxShadow:
            '0 4px 18px rgba(42, 35, 25, 0.1), 0 2px 4px rgba(42, 35, 25, 0.06)',
        },
        elevation8: {
          boxShadow:
            '0 8px 32px rgba(42, 35, 25, 0.14), 0 2px 8px rgba(42, 35, 25, 0.08)',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ...elevatedPaperSurface(theme.palette.background.paper),
          boxShadow: cardShadow,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius * 1.15,
        }),
      },
    },
    MuiButton: {
      // Primary CTAs: use PrimaryActionButton (contained + primary) for filled forest-green actions.
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        containedPrimary: {
          boxShadow: '0 2px 6px rgba(30, 63, 46, 0.25)',
          '&:hover': {
            boxShadow: '0 3px 10px rgba(30, 63, 46, 0.3)',
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'info',
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          fontWeight: 500,
          textDecorationColor: 'currentColor',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'rgba(90, 80, 69, 0.38)',
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(90, 80, 69, 0.55)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: 'rgba(90, 80, 69, 0.09)',
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: 'rgba(90, 80, 69, 0.045)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          // Alert uses Paper; global MuiPaper adds card texture — unreadable under severity colors.
          backgroundImage: 'none',
          backgroundBlendMode: 'normal',
        },
        standardInfo: {
          backgroundColor: 'rgba(42, 107, 107, 0.12)',
          color: '#1a3d3d',
        },
        standardSuccess: {
          backgroundColor: 'rgba(61, 107, 74, 0.14)',
          color: '#1e3326',
        },
        standardWarning: {
          backgroundColor: 'rgba(154, 122, 36, 0.16)',
          color: '#3d3210',
        },
        standardError: {
          backgroundColor: 'rgba(165, 58, 50, 0.12)',
          color: '#4a1f1b',
        },
      },
    },
  },
});

export { muiTheme };
